import { useEffect, useState, useContext } from "react";
import { AxiosError } from "axios";

import { ContinueWatchingDTO} from "../../../../Models/DTOs/VideoDTO";
import { GetContinueWatching} from "../../../../Api/Video";

// <-- Import the auth context
import { UserAuthenticationContext } from "../../../../Context/UserAuthenticationContext";

function useFetchContinueWatching(userId?: string, take: number = 15, skip: number = 0) {
    // <-- Grab the token accessor
    const { getValidAccessToken } = useContext(UserAuthenticationContext);

    const [continueWatching, setContinueWatching] = useState<ContinueWatchingDTO | null>(null);
    const [continueWatchingLoading, setContinueWatchingLoading] = useState<boolean>(false);
    const [continueWatchingError, setContinueWatchingError] = useState<string>("");

    useEffect(() => {
        let isCancelled = false;
        const abort = new AbortController();

        if (!userId) {
            setContinueWatching(null);
            setContinueWatchingLoading(false);
            setContinueWatchingError("");
            return () => abort.abort();
        }

        setContinueWatchingLoading(true);
        setContinueWatchingError("");

        (async () => {
            try {
                // <-- Wait for a valid token before making the request
                const token = await getValidAccessToken();
                if (!token) {
                    if (!isCancelled) {
                        setContinueWatchingError("Unauthorized: No access token");
                        setContinueWatchingLoading(false);
                    }
                    return;
                }

                // <-- Pass the token to the API
                const res = await GetContinueWatching(userId, token, abort, take, skip);
                if (isCancelled) return;

                if ((res as AxiosError)?.isAxiosError) {
                    console.error("useFetchContinueWatching error", res);
                    setContinueWatching(null);
                    setContinueWatchingError("Failed to fetch continue watching data.");
                    return;
                }

                setContinueWatching(res as ContinueWatchingDTO);
            } catch (err) {
                if (!isCancelled) {
                    console.error(err);
                    setContinueWatching(null);
                    setContinueWatchingError("Failed to fetch continue watching data.");
                }
            } finally {
                if (!isCancelled) setContinueWatchingLoading(false);
            }
        })();

        return () => {
            isCancelled = true;
            abort.abort();
        };
    }, [userId, take, skip, getValidAccessToken]);

    return { continueWatching, continueWatchingLoading, continueWatchingError };
}

export default useFetchContinueWatching;
